/*
 * EG gift guard
 *
 * EG Auto Add to Cart intercepts a cart add, posts its gift lines first and then lets
 * the original add through, back to back. Shopify sometimes applies that original add
 * to a cart that does not have the gift yet, so the gift line is lost even though its
 * add returned 200. EG records the rule as triggered and never re-adds it, so the order
 * goes through without the gift.
 *
 * This guard watches EG's own gift adds (`_easygift_internal=true`). Shortly after the
 * shopper's add that triggered them completes, it checks that each gift is still in the
 * cart and re-adds any missing one, once, with the same `_Gifted` property so EG's
 * discount keeps it at $0. It never decides eligibility itself: it only restores what EG
 * already added, so changing gifts or rules in EG needs no theme change.
 *
 * Loaded before content_for_header so it wraps fetch/XHR ahead of the app scripts.
 */
(function () {
  if (window.__egGiftGuard) return;
  window.__egGiftGuard = true;

  var CHECK_DELAY_MS = 300; // after the triggering add completes
  var FALLBACK_MS = 4000; // in case that add went through a path we can't see
  var CONFIRM_MS = 300; // cart reads can lag a write, so confirm before re-adding
  var MAX_AGE_MS = 15000;

  var pending = []; // { id, properties, at }
  var restored = {}; // variant id -> true, one restore per page view
  var timer = null;
  var busy = false;
  var inflight = 0; // non-EG line writes (add/change) still waiting on Shopify

  var nativeFetch = window.fetch.bind(window);

  function isEgRequest(url) {
    return typeof url === 'string' && url.indexOf('_easygift_internal') !== -1;
  }

  function isEgAdd(url) {
    return isEgRequest(url) && url.indexOf('/cart/add') !== -1;
  }

  // EG removing or changing its own lines (cart dropped below a threshold, purchase type
  // switched): those gifts are no longer owed, so stop tracking them.
  function isEgRemoval(url) {
    return isEgRequest(url) && /\/cart\/(change|update)/.test(url);
  }

  function isCartWrite(url) {
    return typeof url === 'string' && /\/cart\/(add|change|update)/.test(url);
  }

  // Writes that can change lines. Attribute-only updates from other apps don't drop gifts.
  function isLineWrite(url) {
    return typeof url === 'string' && /\/cart\/(add|change)/.test(url);
  }

  // Hold the drawer's checkout button (disabled while the cart store is loading) while a
  // missing gift is restored, so the shopper can't check out in that second.
  function holdCheckout(on) {
    try {
      var store = window.Alpine && window.Alpine.store && window.Alpine.store('cart');
      if (store) store.loading = on;
    } catch (e) {}
  }

  // EG posts multipart form data: items[0][id], items[0][quantity], items[0][properties][_Gifted]
  function readGiftLines(body) {
    var lines = [];
    try {
      if (body && typeof body.forEach === 'function' && typeof body.get === 'function') {
        var items = {};
        body.forEach(function (value, key) {
          var m = /^items\[(\d+)\]\[(\w+)\](?:\[(.+)\])?$/.exec(key);
          if (!m) return;
          var item = (items[m[1]] = items[m[1]] || { properties: {} });
          if (m[2] === 'properties' && m[3]) item.properties[m[3]] = String(value);
          else item[m[2]] = String(value);
        });
        Object.keys(items).forEach(function (k) {
          lines.push(items[k]);
        });
      } else if (typeof body === 'string' && body.charAt(0) === '{') {
        var json = JSON.parse(body);
        (json.items || [json]).forEach(function (item) {
          lines.push({ id: String(item.id), properties: item.properties || {} });
        });
      }
    } catch (e) {}
    return lines.filter(function (line) {
      return line.id && line.properties && line.properties._Gifted;
    });
  }

  function record(body) {
    var now = Date.now();
    readGiftLines(body).forEach(function (line) {
      pending.push({ id: line.id, properties: line.properties, at: now });
    });
    if (pending.length) schedule(FALLBACK_MS);
  }

  // Keep the earliest scheduled check; later writes don't push it back.
  var dueAt = 0;
  function schedule(delay) {
    if (!pending.length) return;
    var at = Date.now() + delay;
    if (timer && dueAt <= at) return;
    clearTimeout(timer);
    dueAt = at;
    timer = setTimeout(function () {
      timer = null;
      check();
    }, delay);
  }

  function getCart() {
    return nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function missingFrom(cart, gifts) {
    return gifts.filter(function (g) {
      return !cart.items.some(function (item) {
        return String(item.variant_id) === g.id && item.properties && item.properties._Gifted;
      });
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function check() {
    if (busy || inflight > 0) return schedule(CHECK_DELAY_MS);
    var now = Date.now();
    var gifts = pending.filter(function (g) {
      return now - g.at < MAX_AGE_MS && !restored[g.id];
    });
    pending = [];
    if (!gifts.length) return;
    busy = true;

    getCart()
      .then(function (cart) {
        var missing = missingFrom(cart, gifts);
        if (!missing.length) return;
        holdCheckout(true);
        return wait(CONFIRM_MS)
          .then(getCart)
          .then(function (again) {
            missing = missingFrom(again, missing);
            if (!missing.length) return;
            missing.forEach(function (g) {
              restored[g.id] = true;
            });
            return nativeFetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: missing.map(function (g) {
                  return { id: Number(g.id), quantity: 1, properties: g.properties };
                }),
              }),
            }).then(function (r) {
              if (!r.ok) return;
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
              if (window._cart && typeof window._cart.get === 'function') return window._cart.get();
            });
          });
      })
      .catch(function () {})
      .then(function () {
        holdCheckout(false);
        busy = false;
      });
  }

  function watch(url, body) {
    if (isEgAdd(url)) record(body);
    else if (isEgRemoval(url)) pending = [];
    if (isLineWrite(url) && !isEgRequest(url)) inflight++;
  }

  // Only a non-EG write (the shopper's add) is what can drop a gift, so the check runs
  // shortly after one of those completes.
  function afterWrite(url) {
    if (!isCartWrite(url) || isEgRequest(url)) return;
    if (isLineWrite(url)) inflight = Math.max(0, inflight - 1);
    schedule(CHECK_DELAY_MS);
  }

  // fetch
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    watch(url, init && init.body);
    var result = nativeFetch.apply(window, arguments);
    result.then(
      function () {
        afterWrite(url);
      },
      function () {
        afterWrite(url);
      }
    );
    return result;
  };

  // XMLHttpRequest
  var open = XMLHttpRequest.prototype.open;
  var send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__egUrl = String(url || '');
    return open.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var url = this.__egUrl;
    watch(url, body);
    this.addEventListener('loadend', function () {
      afterWrite(url);
    });
    return send.apply(this, arguments);
  };
})();
